/* eslint-disable @typescript-eslint/no-use-before-define */
import { NestedTableNodeTree } from 'ui/treeView';
import { localizePercent } from 'utils/l10n/percent';
import { getToggleLabel } from '../utils/generalUtils';
import { Row } from '../components';
import { Link, RowTypes, SnipPathToDataMap, SnippetBreakdownResp, Sort, Variation } from '../types';
import { SnippetPathTree, getSnippetPathTree } from './getSnippetPathTree';
import { getCombinedSnippetAndLinkIdOrder, sortRows } from './sortRows';

export const getVariationRow = (variation: Variation) => {
  const {
    url,
    metrics: { TotalClicks, UniqueClicks },
  } = variation;

  return {
    id: url,
    row: <Row cells={[url, '', TotalClicks, UniqueClicks, '']} redirect />,
  };
};

export const getLinkRow = (link: Link, sort: Sort, depth = 0): NestedTableNodeTree => {
  const {
    id,
    templateUrl,
    urlCount,
    variations,
    metrics: { TotalClicks, UniqueClicks },
  } = link;
  const isAtVariationLimit = urlCount > 50;
  const cells = [templateUrl, '', TotalClicks, UniqueClicks, ''];
  if (urlCount === 1 && !templateUrl.includes('{{')) {
    return {
      id,
      row: <Row cells={cells} redirect />,
    };
  }

  const toggleLabel = getToggleLabel(urlCount);

  if (depth >= 3) {
    return {
      id,
      row: <Row cells={cells} toggleLabel={toggleLabel} isAtDepthLimit />,
    };
  }
  const nestedData = isAtVariationLimit
    ? undefined
    : sortRows(variations, sort, RowTypes.Variation).map((v) => getVariationRow(v));

  return {
    id,
    row: (
      <Row
        isExpandable={!isAtVariationLimit}
        cells={cells}
        toggleLabel={toggleLabel}
        isAtVariationLimit={isAtVariationLimit}
      />
    ),
    nestedData,
  };
};

export const getSnippetRow = (snip: SnippetBreakdownResp, depth: number) => {
  const {
    id,
    name,
    urlCount,
    metrics: { TotalSent, TotalClicks, UniqueClicks, ClickRate },
  } = snip;

  const cells = [name, TotalSent, TotalClicks, UniqueClicks, localizePercent(ClickRate)];
  const toggleLabel = getToggleLabel(urlCount);

  if (depth >= 3) {
    return <Row cells={cells} snippetId={id} toggleLabel={toggleLabel} isAtDepthLimit />;
  }
  return <Row cells={cells} snippetId={id} toggleLabel={toggleLabel} isExpandable />;
};

export const getSnippetRowData = (
  snip: SnippetBreakdownResp,
  children: SnippetPathTree[],
  snipPathToDataMap: SnipPathToDataMap,
  sort: Sort,
  depth = 0,
): NestedTableNodeTree => ({
  id: snip.snippetPath,
  row: getSnippetRow(snip, depth),
  nestedData:
    depth < 3 ? getAllNestedRows(snip, children, snipPathToDataMap, sort, depth + 1) : undefined,
});

// combination of child snippets (if any) and links
export const getAllNestedRows = (
  snip: SnippetBreakdownResp,
  children: SnippetPathTree[],
  snipPathToDataMap: SnipPathToDataMap,
  sort: Sort,
  depth = 0,
): NestedTableNodeTree[] => {
  const nestedSnippets = children.reduce(
    (rows, child) => [
      ...rows,
      getSnippetRowData(
        snipPathToDataMap[child.path],
        child.children,
        snipPathToDataMap,
        sort,
        depth,
      ),
    ],
    [] as NestedTableNodeTree[],
  );
  const linkData = snip.links;
  const links = linkData.map((link) => getLinkRow(link, sort, depth));
  const combinedRows = [...nestedSnippets, ...links];
  const sortOrder = getCombinedSnippetAndLinkIdOrder(linkData, children, snipPathToDataMap, sort);

  return sortOrder.reduce((sortedRows, id) => {
    const row = combinedRows.find((r) => r.id === id);
    return row ? [...sortedRows, row] : sortedRows;
  }, [] as NestedTableNodeTree[]);
};

export const getSnippetBreakdownRows = (
  breakdown: SnippetBreakdownResp[] | undefined,
  baseLevelSnipId: string,
  sort: Sort,
) => {
  if (!breakdown) return undefined;

  const paths = breakdown.map((snip) => snip.snippetPath);
  const tree = getSnippetPathTree(paths, baseLevelSnipId);

  const snipPathToDataMap = breakdown.reduce(
    (map, snip) => ({ ...map, [snip.snippetPath]: snip }),
    {} as SnipPathToDataMap,
  );

  return getSnippetRowData(
    snipPathToDataMap[baseLevelSnipId],
    tree.children,
    snipPathToDataMap,
    sort,
  );
};
